/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SelectModule.tsx
 🗓️ Created: 2025-01-14
 🧩 Description:
    Reusable component for selecting a module type that will
    determine what destinations are available in SelectDestination.

 🔧 Notes:
    - Searchable select component
    - Currently hardcoded with "Terminate Call" module
    - Will be expanded to support more modules (Extensions, IVRs, etc.)

 🔁 Last Updated: 2025-01-14 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';

interface Module {
  value: string;
  label: string;
  description?: string;
}

interface SelectModuleProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

// Available module types
const AVAILABLE_MODULE_KEYS = [
  'extension',
  'conference',
  'direct_dial',
  'direct_route',
  'paging',
  'parking',
  'disa',
  'ring_group',
  'queue',
  'ivr',
  'time_condition',
  'announcement',
  'language',
  'call_flow',
  'authentication_code',
  'ai_agent',
  'callback_rules',
  'custom_application',
  'voicemail',
  'fax',
  'terminate_call'
];

export default function SelectModule({
  value,
  onChange,
  placeholder = 'Select Module',
  disabled = false,
  className = '',
  id
}: SelectModuleProps) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);

  // Build modules array from translations
  const availableModules: Module[] = AVAILABLE_MODULE_KEYS.map(key => ({
    value: key,
    label: t(`common.modules.${key}.label`),
    description: t(`common.modules.${key}.description`)
  }));

  // Get selected module
  const selectedModule = availableModules.find(m => m.value === value);

  // Filter modules based on search
  const filteredModules = availableModules.filter(module =>
    module.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (module.description && module.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current && dropdownContentRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (buttonRef.current && dropdownContentRef.current) {
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const dropdownRect = dropdownContentRef.current.getBoundingClientRect();
          const dropdownHeight = dropdownRect.height || 240; // Fallback to approximate height
          const spaceBelow = window.innerHeight - buttonRect.bottom;
          const spaceAbove = buttonRect.top;

          // Open upward if there's not enough space below but enough space above
          if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            setOpenUpward(true);
          } else {
            setOpenUpward(false);
          }
        }
      });
    }
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (moduleValue: string) => {
    onChange(moduleValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        id={id}
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-md
          bg-background text-foreground
          border-input
          hover:bg-accent hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between
          transition-colors
        `}
      >
        <span className={selectedModule ? '' : 'text-muted-foreground'}>
          {selectedModule ? selectedModule.label : placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownContentRef}
          className={`absolute z-50 w-full bg-popover border border-border rounded-md shadow-lg ${openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search modules..."
              className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto p-1">
            {filteredModules.length > 0 ? (
              filteredModules.map((module) => (
                <button
                  key={module.value}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(module.value);
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  className={`
                    w-full px-3 py-2 text-left rounded-md cursor-pointer
                    transition-colors flex items-center justify-between
                    ${value === module.value 
                      ? 'bg-primary/20 text-primary font-semibold' 
                      : 'hover:bg-primary/15 text-foreground'}
                  `}
                >
                  <div className="flex-1">
                    <div className="font-medium">{module.label}</div>
                    {module.description && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {module.description}
                      </div>
                    )}
                  </div>
                  {value === module.value && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No modules found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

